import React, { useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import UseAuth from '../hooks/UseAuth';
import logo from '../assets/op.png';
import {
    LayoutDashboard,
    IceCream,
    Users,
    LogOut,
    CreditCard,
    ChefHat,
    QrCode,
    Package,
    ShoppingCart,
    UtensilsCrossed,
    BookOpen,
    PlusCircle,
    FileText,
    ChevronDown,
    Layers,
    Boxes,
    Truck,
    Settings,
    Building2,
    ClipboardCheck,
    Briefcase,
    Clock,
    Calendar,
    FileSpreadsheet, // 👈 NEW: Imported for Attendance Sheet
    Newspaper
} from 'lucide-react';

const Sidebar = () => {
    const { user, logOutUser: logout } = UseAuth();
    const userRole = user?.role || 'user';
    const navigate = useNavigate();
    const { pathname } = useLocation();

    const [openGroup, setOpenGroup] = useState('Operations');

    // Group Definitions (Perfectly Sorted!)
    const navGroups = [
        {
            groupName: 'Operations',
            icon: <Layers size={20} />,
            items: [
                { path: '/admin/pos', name: 'POS Terminal', icon: <CreditCard size={20} />, roles: ['admin', 'owner', 'staff'] },
                { path: '/admin/kitchen', name: 'Kitchen Monitor', icon: <ChefHat size={20} />, roles: ['admin', 'owner', 'staff'] },
                { path: '/admin/tables', name: 'Tables & QR', icon: <QrCode size={20} />, roles: ['admin', 'owner'] }
            ]
        },
        {
            groupName: 'Inventory',
            icon: <Boxes size={20} />,
            items: [
                { path: '/admin/product_stock', name: 'Finished Stock', icon: <IceCream size={20} />, roles: ['admin', 'owner'] },
                { path: '/admin/produce', name: 'Production', icon: <UtensilsCrossed size={20} />, roles: ['admin', 'owner'] },
                { path: '/admin/add_recipe', name: 'Add Recipe', icon: <BookOpen size={20} />, roles: ['admin', 'owner'] },
                { path: '/admin/recipe_list', name: 'Recipes', icon: <BookOpen size={20} />, roles: ['admin', 'owner'] }
            ]
        },
        {
            groupName: 'Supply Chain',
            icon: <Truck size={20} />,
            items: [
                { path: '/admin/list_ingredient', name: 'Ingredients', icon: <Package size={20} />, roles: ['admin', 'owner'] },
                { path: '/admin/purchase_ingredient', name: 'Purchase', icon: <ShoppingCart size={20} />, roles: ['admin', 'owner'] },
                { path: '/admin/add_ingredient', name: 'Add Item', icon: <PlusCircle size={20} />, roles: ['admin', 'owner'] },
                { path: '/admin/sales-report', name: 'Sales Report', icon: <FileText size={20} />, roles: ['admin', 'owner'] },
                { path: '/admin/qc-pass', name: 'QC Dashboard', icon: <ClipboardCheck size={20} />, roles: ['admin', 'owner'] }
            ]
        },
        {
            groupName: 'Admin Panel',
            icon: <Settings size={20} />,
            items: [
                { path: '/admin/branches', name: 'Locations', icon: <Building2 size={20} />, roles: ['admin', 'owner'] },
                { path: '/admin/user_manage', name: 'Users', icon: <Users size={20} />, roles: ['admin', 'owner'] },
                { path: '/admin/hr', name: 'HR Roster', icon: <Briefcase size={20} />, roles: ['admin', 'owner'] }, // 👈 MOVED HERE
                { path: '/admin/attendance-sheet', name: 'Attendance Sheet', icon: <FileSpreadsheet size={20} />, roles: ['admin', 'owner'] }, // 👈 ADDED HERE
                { path: '/admin/leave-management', name: 'Leave Requests', icon: <Calendar size={20} />, roles: ['admin', 'owner'] },
                { path: '/admin/salary-sheet', name: 'Salary Sheet', icon: <FileText size={20} />, roles: ['admin', 'owner'] },
                { path: '/admin/employee-ledger', name: 'Employee Ledger', icon: <BookOpen size={20} />, roles: ['admin', 'owner'] },
                { path: '/admin/blog-list', name: 'Blog Posts', icon: <Newspaper size={20} />, roles: ['admin', 'owner'] }, // 👈 NEW
                { path: '/admin/add-blog', name: 'Create Post', icon: <FileText size={20} />, roles: ['admin', 'owner'] },  // 👈 NEW
                { path: '/admin/expense-ledger', name: 'Expense Ledger', icon: <FileText size={20} />, roles: ['admin', 'owner'] },  // 👈 NEW
                { path: '/admin/true-roi', name: 'ROI', icon: <FileText size={20} />, roles: ['admin', 'owner'] },  // 👈 NEW
            ]
        },
        {
            groupName: 'Staff Portal',
            icon: <Clock size={20} />,
            items: [
                { path: '/admin/staff-dashboard', name: 'My Workspace', icon: <Clock size={20} />, roles: ['staff'] } // 👈 Moved into its own clean group so staff don't see an "Admin Panel"
            ]
        }
    ];

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    return (
        <aside className="hidden md:flex flex-col w-64 h-[95vh] fixed left-4 top-1/2 -translate-y-1/2 rounded-3xl bg-[#1a103c]/60 backdrop-blur-2xl border border-white/10 shadow-[0_0_40px_rgba(0,0,0,0.5)] z-50 overflow-hidden">

            {/* Logo Area */}
            <div className="h-24 flex flex-col items-center justify-center border-b border-white/5 bg-gradient-to-b from-white/5 to-transparent">
                <div className="p-2 bg-white rounded-xl mb-1">
                    <img src={logo} alt="Logo" className="w-24 h-full object-contain" />
                </div>

            </div>

            {/* Navigation */}
            <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto custom-scrollbar">

                {/* Standalone Overview */}
                {userRole !== 'staff' && (
                    <NavLink
                        to="/admin"
                        end
                        className={({ isActive }) => `
                            relative flex items-center gap-3 px-4 py-3 rounded-2xl transition-all duration-300 group overflow-hidden
                            ${isActive
                                ? 'bg-gradient-to-r from-pink-600 via-purple-600 to-indigo-600 text-white shadow-lg shadow-purple-500/20'
                                : 'text-slate-400 hover:bg-white/5 hover:text-white'
                            }
                        `}
                    >
                        <div className="absolute inset-0 bg-white/5 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000 transform skew-x-12"></div>
                        <span className={`relative z-10 transition-transform duration-300 ${pathname === '/admin' ? 'scale-110' : 'group-hover:scale-110'}`}>
                            <LayoutDashboard size={20} />
                        </span>
                        <span className="relative z-10 font-bold text-xs uppercase tracking-wide">Overview</span>
                    </NavLink>
                )}

                {/* Grouped Items */}
                {navGroups.map((group) => {
                    const filteredItems = group.items.filter(item => item.roles.includes(userRole));
                    if (filteredItems.length === 0) return null;

                    const isOpen = openGroup === group.groupName;

                    return (
                        <div key={group.groupName} className="pt-2">
                            <button
                                onClick={() => setOpenGroup(isOpen ? null : group.groupName)}
                                className="w-full flex items-center gap-3 px-4 py-2 text-white/70 hover:text-white transition-all group"
                            >
                                <span className="group-hover:scale-110 transition-transform">{group.icon}</span>
                                <span className="flex-1 text-left font-bold text-xs uppercase tracking-wide">
                                    {group.groupName}
                                </span>
                                <ChevronDown size={16} className={`transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
                            </button>

                            <div className={`space-y-1 overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-[600px] opacity-100 mt-2' : 'max-h-0 opacity-0'}`}>
                                {filteredItems.map((item) => (
                                    <NavLink
                                        key={item.path}
                                        to={item.path}
                                        className={({ isActive }) => `
                                            relative flex items-center gap-3 px-4 py-3 rounded-2xl transition-all duration-300 group overflow-hidden
                                            ${isActive
                                                ? 'bg-gradient-to-r from-pink-600 via-purple-600 to-indigo-600 text-white shadow-lg shadow-purple-500/20'
                                                : 'text-slate-400 hover:bg-white/5 hover:text-white'
                                            }
                                        `}
                                    >
                                        <div className="absolute inset-0 bg-white/5 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000 transform skew-x-12"></div>
                                        <span className={`relative z-10 transition-transform duration-300 ${pathname === item.path ? 'scale-110' : 'group-hover:scale-110'}`}>
                                            {item.icon}
                                        </span>
                                        <span className="relative z-10 font-bold text-xs uppercase tracking-wide">{item.name}</span>
                                    </NavLink>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </nav>

            {/* Logout */}
            <div className="p-4 bg-gradient-to-t from-black/40 to-transparent">
                <button
                    onClick={handleLogout}
                    className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-white/5 border border-white/5 hover:bg-red-500/20 hover:border-red-500/30 hover:text-red-400 text-slate-400 transition-all duration-300 group"
                >
                    <LogOut size={18} className="group-hover:-rotate-12 transition-transform" />
                    <span className="font-bold text-xs uppercase tracking-wider">Log Out</span>
                </button>
            </div>
        </aside>
    );
};

export default Sidebar;